import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ShoppingCart, Trash2, ArrowLeft, CheckCircle, Package, ExternalLink, Wallet, AlertTriangle, Percent, Ticket, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const translations = {
  es: {
    title: 'Carrito de Compra',
    empty: 'Tu carrito está vacío',
    emptyDesc: 'Añade productos del catálogo para comenzar',
    browseCatalog: 'Explorar Catálogo',
    item: 'producto',
    items: 'productos',
    priceType: 'Tipo de precio',
    normal: 'Normal',
    sensitive: 'Sensible',
    subtotal: 'Subtotal',
    total: 'Total',
    remove: 'Eliminar',
    clear: 'Vaciar carrito',
    checkout: 'Realizar Pedido',
    processing: 'Procesando...',
    orderSuccess: '¡Pedido realizado con éxito!',
    orderSuccessDesc: 'Recibirás un email de confirmación con los detalles de tu pedido.',
    continue: 'Continuar comprando',
    confirmClear: '¿Vaciar carrito?',
    confirmClearDesc: '¿Estás seguro de que quieres eliminar todos los productos del carrito?',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    ordersCreated: 'Pedidos creados',
    orderNumbers: 'Números',
    orderNote: 'Se creará un pedido por cada producto y recibirás confirmación por email.',
    country: 'País',
    dr: 'DR',
    walletBalance: 'Saldo del monedero',
    insufficientBalance: 'Saldo insuficiente',
    insufficientBalanceDesc: 'No tienes suficiente saldo para completar este pedido. Por favor, recarga tu monedero.',
    rechargeWallet: 'Recargar Monedero',
    neededAmount: 'Importe necesario',
  },
  en: {
    title: 'Shopping Cart',
    empty: 'Your cart is empty',
    emptyDesc: 'Add products from the catalog to get started',
    browseCatalog: 'Browse Catalog',
    item: 'item',
    items: 'items',
    priceType: 'Price type',
    normal: 'Normal',
    sensitive: 'Sensitive',
    subtotal: 'Subtotal',
    total: 'Total',
    remove: 'Remove',
    clear: 'Clear cart',
    checkout: 'Place Order',
    processing: 'Processing...',
    orderSuccess: 'Order placed successfully!',
    orderSuccessDesc: 'You will receive a confirmation email with your order details.',
    continue: 'Continue shopping',
    confirmClear: 'Clear cart?',
    confirmClearDesc: 'Are you sure you want to remove all products from your cart?',
    cancel: 'Cancel',
    confirm: 'Confirm',
    ordersCreated: 'Orders created',
    orderNumbers: 'Numbers',
    orderNote: 'An order will be created for each product and you will receive confirmation by email.',
    country: 'Country',
    dr: 'DR',
    walletBalance: 'Wallet balance',
    insufficientBalance: 'Insufficient balance',
    insufficientBalanceDesc: 'You don\'t have enough balance to complete this order. Please recharge your wallet.',
    rechargeWallet: 'Recharge Wallet',
    neededAmount: 'Amount needed',
  },
  de: {
    title: 'Warenkorb',
    empty: 'Ihr Warenkorb ist leer',
    emptyDesc: 'Fügen Sie Produkte aus dem Katalog hinzu',
    browseCatalog: 'Katalog durchsuchen',
    item: 'Produkt',
    items: 'Produkte',
    priceType: 'Preistyp',
    normal: 'Normal',
    sensitive: 'Sensibel',
    subtotal: 'Zwischensumme',
    total: 'Gesamt',
    remove: 'Entfernen',
    clear: 'Warenkorb leeren',
    checkout: 'Bestellung aufgeben',
    processing: 'Verarbeitung...',
    orderSuccess: 'Bestellung erfolgreich aufgegeben!',
    orderSuccessDesc: 'Sie erhalten eine Bestätigungs-E-Mail mit Ihren Bestelldetails.',
    continue: 'Weiter einkaufen',
    confirmClear: 'Warenkorb leeren?',
    confirmClearDesc: 'Sind Sie sicher, dass Sie alle Produkte aus dem Warenkorb entfernen möchten?',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    ordersCreated: 'Bestellungen erstellt',
    orderNumbers: 'Nummern',
    orderNote: 'Für jedes Produkt wird eine Bestellung erstellt und Sie erhalten eine Bestätigung per E-Mail.',
    country: 'Land',
    dr: 'DR',
    walletBalance: 'Wallet-Guthaben',
    insufficientBalance: 'Unzureichendes Guthaben',
    insufficientBalanceDesc: 'Sie haben nicht genügend Guthaben. Bitte laden Sie Ihre Wallet auf.',
    rechargeWallet: 'Wallet aufladen',
    neededAmount: 'Benötigter Betrag',
  },
  fr: {
    title: 'Panier',
    empty: 'Votre panier est vide',
    emptyDesc: 'Ajoutez des produits du catalogue pour commencer',
    browseCatalog: 'Parcourir le catalogue',
    item: 'produit',
    items: 'produits',
    priceType: 'Type de prix',
    normal: 'Normal',
    sensitive: 'Sensible',
    subtotal: 'Sous-total',
    total: 'Total',
    remove: 'Supprimer',
    clear: 'Vider le panier',
    checkout: 'Passer la commande',
    processing: 'Traitement...',
    orderSuccess: 'Commande passée avec succès!',
    orderSuccessDesc: 'Vous recevrez un email de confirmation avec les détails de votre commande.',
    continue: 'Continuer les achats',
    confirmClear: 'Vider le panier?',
    confirmClearDesc: 'Êtes-vous sûr de vouloir supprimer tous les produits du panier?',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    ordersCreated: 'Commandes créées',
    orderNumbers: 'Numéros',
    orderNote: 'Une commande sera créée pour chaque produit et vous recevrez une confirmation par email.',
    country: 'Pays',
    dr: 'DR',
    walletBalance: 'Solde du portefeuille',
    insufficientBalance: 'Solde insuffisant',
    insufficientBalanceDesc: 'Vous n\'avez pas assez de solde. Veuillez recharger votre portefeuille.',
    rechargeWallet: 'Recharger le portefeuille',
    neededAmount: 'Montant nécessaire',
  },
  it: {
    title: 'Carrello',
    empty: 'Il tuo carrello è vuoto',
    emptyDesc: 'Aggiungi prodotti dal catalogo per iniziare',
    browseCatalog: 'Sfoglia il catalogo',
    item: 'prodotto',
    items: 'prodotti',
    priceType: 'Tipo di prezzo',
    normal: 'Normale',
    sensitive: 'Sensibile',
    subtotal: 'Subtotale',
    total: 'Totale',
    remove: 'Rimuovi',
    clear: 'Svuota carrello',
    checkout: 'Effettua ordine',
    processing: 'Elaborazione...',
    orderSuccess: 'Ordine effettuato con successo!',
    orderSuccessDesc: 'Riceverai un\'email di conferma con i dettagli del tuo ordine.',
    continue: 'Continua lo shopping',
    confirmClear: 'Svuotare il carrello?',
    confirmClearDesc: 'Sei sicuro di voler rimuovere tutti i prodotti dal carrello?',
    cancel: 'Annulla',
    confirm: 'Conferma',
    ordersCreated: 'Ordini creati',
    orderNumbers: 'Numeri',
    orderNote: 'Verrà creato un ordine per ogni prodotto e riceverai conferma via email.',
    country: 'Paese',
    dr: 'DR',
    walletBalance: 'Saldo portafoglio',
    insufficientBalance: 'Saldo insufficiente',
    insufficientBalanceDesc: 'Non hai abbastanza saldo. Ricarica il tuo portafoglio.',
    rechargeWallet: 'Ricarica portafoglio',
    neededAmount: 'Importo necessario',
  },
  pt: {
    title: 'Carrinho de Compras',
    empty: 'Seu carrinho está vazio',
    emptyDesc: 'Adicione produtos do catálogo para começar',
    browseCatalog: 'Explorar Catálogo',
    item: 'produto',
    items: 'produtos',
    priceType: 'Tipo de preço',
    normal: 'Normal',
    sensitive: 'Sensível',
    subtotal: 'Subtotal',
    total: 'Total',
    remove: 'Remover',
    clear: 'Limpar carrinho',
    checkout: 'Fazer Pedido',
    processing: 'Processando...',
    orderSuccess: 'Pedido realizado com sucesso!',
    orderSuccessDesc: 'Você receberá um email de confirmação com os detalhes do seu pedido.',
    continue: 'Continuar comprando',
    confirmClear: 'Limpar carrinho?',
    confirmClearDesc: 'Tem certeza de que deseja remover todos os produtos do carrinho?',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    ordersCreated: 'Pedidos criados',
    orderNumbers: 'Números',
    orderNote: 'Um pedido será criado para cada produto e você receberá confirmação por email.',
    country: 'País',
    dr: 'DR',
    walletBalance: 'Saldo da carteira',
    insufficientBalance: 'Saldo insuficiente',
    insufficientBalanceDesc: 'Você não tem saldo suficiente. Por favor, recarregue sua carteira.',
    rechargeWallet: 'Recarregar Carteira',
    neededAmount: 'Valor necessário',
  },
};

const CartPage = () => {
  const { cartItems, removeFromCart, clearCart, checkout, getCartTotal, loading } = useCart();
  const { token } = useAuth();
  const { language, tx } = useAdminTranslation();
  const t = translations[language] || translations.es;
  const navigate = useNavigate();
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  
  // Coupon state - use context for persistence across navigation
  const [couponCode, setCouponCode] = useState('');
  const { appliedCoupon, setAppliedCoupon } = useCart();
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Fetch wallet balance and user discount
  useEffect(() => {
    const fetchWalletAndDiscount = async () => {
      if (!token) {
        setLoadingWallet(false);
        return;
      }
      
      try {
        // Fetch wallet
        const walletResponse = await fetch(`${API_URL}/api/wallet`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (walletResponse.ok) {
          const walletData = await walletResponse.json();
          setWalletBalance(walletData.balance || 0);
        }
        
        // Fetch profile to get discount
        const profileResponse = await fetch(`${API_URL}/api/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          setDiscountPercentage(profileData.discount_percentage || 0);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingWallet(false);
      }
    };
    fetchWalletAndDiscount();
  }, [token]);

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: couponCode.toUpperCase() })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check min purchase
        if (data.min_purchase > 0 && subtotal < data.min_purchase) {
          setCouponError(tx(`Compra mínima de ${data.min_purchase}€ requerida`, `Minimum purchase of ${data.min_purchase}€ required`));
          return;
        }
        
        setAppliedCoupon(data);
        setCouponCode('');
      } else {
        const error = await response.json();
        setCouponError(error.detail || (tx('Cupón no válido', 'Invalid coupon')));
      }
    } catch (error) {
      setCouponError(tx('Error al validar cupón', 'Error validating coupon'));
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  // Calculate totals with discount
  const subtotal = getCartTotal();
  const userDiscountAmount = discountPercentage > 0 ? subtotal * (discountPercentage / 100) : 0;
  
  // Calculate coupon discount
  let couponDiscountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.discount_type === 'percentage') {
      couponDiscountAmount = subtotal * (appliedCoupon.discount_value / 100);
    } else {
      couponDiscountAmount = Math.min(appliedCoupon.discount_value, subtotal);
    }
  }
  
  const totalDiscount = userDiscountAmount + couponDiscountAmount;
  const totalWithDiscount = subtotal - totalDiscount;
  const hasInsufficientBalance = walletBalance < totalWithDiscount;

  const handleCheckout = async () => {
    const result = await checkout({ coupon_code: appliedCoupon?.code || null });
    if (result.success) {
      setOrderSuccess(true);
      setOrderData(result.data);
      setAppliedCoupon(null);
    }
  };

  const handleClearCart = async () => {
    await clearCart();
    setShowClearDialog(false);
  };

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h1 className="text-2xl font-bold mb-3">{t.orderSuccess}</h1>
            <p className="text-muted-foreground mb-8">{t.orderSuccessDesc}</p>
            {orderData && (
              <div className="mb-8 p-4 bg-muted rounded-lg text-sm">
                <p className="font-medium">{t.ordersCreated}: {orderData.orders_created}</p>
                {orderData.order_numbers && (
                  <p className="text-muted-foreground">{t.orderNumbers}: {orderData.order_numbers.join(', ')}</p>
                )}
              </div>
            )}
            <Button onClick={() => navigate('/')} size="lg">
              {t.continue}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">{t.title}</h1>
          <span className="text-muted-foreground">({cartItems.length} {cartItems.length === 1 ? t.item : t.items})</span>
        </div>

        {cartItems.length === 0 ? (
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-12 text-center">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">{t.empty}</h2>
              <p className="text-muted-foreground mb-6">{t.emptyDesc}</p>
              <Button onClick={() => navigate('/')}>{t.browseCatalog}</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-primary" />
                          <Link 
                            to={`/product/${item.product_id}`}
                            className="font-semibold hover:text-primary transition-colors"
                          >
                            {item.url || item.website}
                          </Link>
                          <a 
                            href={`https://${item.url || item.website}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-primary"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          {item.country && <span>{t.country}: {item.country}</span>}
                          {item.dr > 0 && <span>{t.dr}: {item.dr}</span>}
                          <span className="capitalize">
                            {t.priceType}: {item.price_type === 'sensitive' ? t.sensitive : t.normal}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary">{item.price?.toFixed(2)} €</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive mt-2"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t.remove}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowClearDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t.clear}
              </Button>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>{t.subtotal}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="truncate max-w-[180px]">{item.url || item.website}</span>
                        <span>{item.price?.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Coupon input */}
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm">{tx('¿Tienes un cupón?', 'Have a coupon?')}</Label>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Ticket className="h-4 w-4 text-green-600" />
                          <code className="font-mono font-bold text-green-700">{appliedCoupon.code}</code>
                          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                            {appliedCoupon.discount_type === 'percentage' ? `-${appliedCoupon.discount_value}%` : `-${appliedCoupon.discount_value}€`}
                          </Badge>
                        </div>
                        <Button variant="ghost" size="sm" onClick={removeCoupon} className="h-6 w-6 p-0 text-red-500 hover:text-red-700">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                          placeholder="CODIGO"
                          className="font-mono text-sm"
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                        >
                          {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (tx('Aplicar', 'Apply'))}
                        </Button>
                      </div>
                    )}
                    {couponError && (
                      <p className="text-xs text-red-600">{couponError}</p>
                    )}
                  </div>
                  
                  <hr />
                  
                  {/* Discount section */}
                  {(discountPercentage > 0 || appliedCoupon) && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>{t.subtotal}</span>
                        <span>{subtotal.toFixed(2)} €</span>
                      </div>
                      {discountPercentage > 0 && (
                        <div className="flex justify-between text-sm text-green-600 font-medium">
                          <span>{tx(`Dto. cliente (${discountPercentage}%)`, `Customer disc. (${discountPercentage}%)`)}</span>
                          <span>-{userDiscountAmount.toFixed(2)} €</span>
                        </div>
                      )}
                      {appliedCoupon && (
                        <div className="flex justify-between text-sm text-green-600 font-medium">
                          <span>{tx(`Cupón ${appliedCoupon.code}`, `Coupon ${appliedCoupon.code}`)}</span>
                          <span>-{couponDiscountAmount.toFixed(2)} €</span>
                        </div>
                      )}
                      <hr />
                    </>
                  )}
                  
                  <div className="flex justify-between font-bold text-lg">
                    <span>{t.total}</span>
                    <span className="text-primary">{totalWithDiscount.toFixed(2)} €</span>
                  </div>
                  
                  {/* Discount badges */}
                  {discountPercentage > 0 && (
                    <div className="flex items-center justify-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <Percent className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">
                        {tx(`¡Tienes un ${discountPercentage}% de descuento!`, `You have a ${discountPercentage}% discount!`)}
                      </span>
                    </div>
                  )}
                  
                  {/* Wallet Balance Info */}
                  {!loadingWallet && (
                    <div className={`flex justify-between items-center text-sm p-2 rounded ${hasInsufficientBalance ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                      <span className="flex items-center gap-1">
                        <Wallet className="h-4 w-4" />
                        {t.walletBalance}:
                      </span>
                      <span className="font-semibold">{walletBalance.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  {/* Insufficient Balance Warning */}
                  {hasInsufficientBalance && !loadingWallet && (
                    <Alert variant="destructive" className="border-red-300 bg-red-50">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        <p className="font-semibold">{t.insufficientBalance}</p>
                        <p className="text-xs mt-1">{t.insufficientBalanceDesc}</p>
                        <p className="text-xs mt-1 font-medium">
                          {t.neededAmount}: {(totalWithDiscount - walletBalance).toFixed(2)} €
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {hasInsufficientBalance ? (
                    <Button 
                      className="w-full bg-amber-500 hover:bg-amber-600" 
                      size="lg"
                      onClick={() => navigate('/wallet')}
                    >
                      <Wallet className="mr-2 h-4 w-4" />
                      {t.rechargeWallet}
                    </Button>
                  ) : (
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={() => navigate('/checkout')}
                      disabled={loading}
                    >
                      {loading ? t.processing : t.checkout}
                    </Button>
                  )}
                  <p className="text-xs text-center text-muted-foreground">
                    {t.orderNote}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Clear Cart Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.confirmClear}</AlertDialogTitle>
            <AlertDialogDescription>{t.confirmClearDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearCart} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CartPage;
